import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateRelative } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Shield, AlertTriangle, FileWarning, CheckCircle2, Plus, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

const RISK_BADGE = {
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    high: "bg-orange-50 text-orange-700 border-orange-300",
    prohibited: "bg-red-50 text-red-700 border-red-200",
    unrated: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

const STATUS_BADGE = {
    complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    expired: "bg-red-50 text-red-700 border-red-200",
    not_started: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

export default function Compliance() {
    const { user } = useAuth();
    if (user?.role === "external_viewer") return <Navigate to="/" replace />;
    const isMlro = user?.role === "admin" || user?.role === "super_admin" || user?.is_mlro;

    return (
        <div data-testid="compliance-page">
            <PageHeader title="Compliance" subtitle="AML / KYC, Customer Due Diligence and Suspicious Activity Reports">
                <Badge className="bg-foreground text-background gap-1.5">
                    <Shield className="w-3 h-3" /> Compliance
                </Badge>
            </PageHeader>
            <div className="p-6 lg:p-8 space-y-6">
                <Dashboard />
                <Tabs defaultValue="cdd">
                    <TabsList data-testid="compliance-tabs" className="bg-transparent border-b border-[#E2E8F0] rounded-none h-auto p-0 gap-0 w-full justify-start">
                        <TabsTrigger value="cdd" data-testid="tab-cdd" className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#0B1F3A] data-[state=active]:text-[#0B1F3A] data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:shadow-none text-[#64748B] text-[13px] px-3 py-2 -mb-px h-auto">CDD records</TabsTrigger>
                        {isMlro && <TabsTrigger value="sar" data-testid="tab-sar" className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#0B1F3A] data-[state=active]:text-[#0B1F3A] data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:shadow-none text-[#64748B] text-[13px] px-3 py-2 -mb-px h-auto"><Lock className="w-3 h-3 mr-1" />SAR log</TabsTrigger>}
                    </TabsList>
                    <TabsContent value="cdd" className="mt-4"><CddTab /></TabsContent>
                    {isMlro && <TabsContent value="sar" className="mt-4"><SarTab /></TabsContent>}
                </Tabs>
            </div>
        </div>
    );
}

function Dashboard() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/compliance/dashboard").then((r) => setS(r.data)); }, []);
    if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Overdue CDD" value={s.overdue_cdd_count} icon={<AlertTriangle className="w-3.5 h-3.5 text-red-600" />} testid="stat-overdue-cdd" warn={s.overdue_cdd_count > 0} />
            <Stat label="Pending CDD" value={s.pending_cdd_count} testid="stat-pending-cdd" />
            <Stat label="Open SARs" value={s.open_sars} icon={<FileWarning className="w-3.5 h-3.5 text-orange-600" />} testid="stat-open-sars" />
            <Stat label="No engagement letter" value={s.deals_missing_engagement_letter} testid="stat-no-eng-letter" warn={s.deals_missing_engagement_letter > 0} />
            <Stat label="Exclusivity expiring (14d)" value={s.deals_expiring_exclusivity_14d} testid="stat-excl-expiring" warn={s.deals_expiring_exclusivity_14d > 0} />
            <Stat label="Eng. letters expiring (14d)" value={s.engagement_letters_expiring_14d} testid="stat-el-expiring" warn={s.engagement_letters_expiring_14d > 0} />
        </div>
    );
}

function Stat({ label, value, icon, warn, testid }) {
    return (
        <div className={`border rounded-md p-4 bg-white ${warn ? "border-orange-300" : "border-border"}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                {icon} {label}
            </div>
            <div className={`mt-1 text-2xl font-bold font-mono ${warn ? "text-orange-700" : "text-navy"}`}>{value}</div>
        </div>
    );
}

function CddTab() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const refresh = async () => setRecords((await api.get("/compliance/cdd")).data);
    useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

    const filtered = filter === "all" ? records : records.filter((r) => r.cdd_status === filter);

    if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap border-b border-[#E2E8F0]">
                {["all", "complete", "pending", "expired", "not_started"].map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setFilter(s)}
                        data-testid={`cdd-filter-${s}`}
                        className={`text-[13px] px-3 py-2 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                            filter === s
                                ? "border-[#0B1F3A] text-[#0B1F3A] font-semibold"
                                : "border-transparent text-[#64748B] hover:text-[#0B1F3A]"
                        }`}
                    >
                        {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ")} ({s === "all" ? records.length : records.filter((r) => r.cdd_status === s).length})
                    </button>
                ))}
            </div>
            {filtered.length === 0 ? (
                <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground" data-testid="cdd-empty">
                    No CDD records yet. Create one from a Contact's profile page.
                </div>
            ) : (
                <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="cdd-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-4 py-3">Contact</th>
                                <th className="text-left px-4 py-3">Risk</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Last reviewed</th>
                                <th className="text-left px-4 py-3">Expires</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r, i) => (
                                <motion.tr key={r.id}
                                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: Math.min(i * 0.02, 0.2), duration: 0.2 }}
                                    className="border-b border-border/60 last:border-0 hover:bg-muted/30"
                                    data-testid={`cdd-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <Link to={`/contacts/${r.contact_id}`} className="font-medium hover:text-gold transition-colors">
                                            {r.contact_name}
                                        </Link>
                                        {r.contact_type && <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{r.contact_type}</div>}
                                    </td>
                                    <td className="px-4 py-3"><Badge className={`text-[10px] uppercase tracking-wider border ${RISK_BADGE[r.risk_rating] || ""}`}>{r.risk_rating}</Badge></td>
                                    <td className="px-4 py-3"><Badge className={`text-[10px] uppercase tracking-wider border ${STATUS_BADGE[r.cdd_status] || ""}`}>{r.cdd_status}</Badge></td>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{r.cdd_date ? new Date(r.cdd_date).toLocaleDateString() : "—"}</td>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{r.cdd_expiry_date ? new Date(r.cdd_expiry_date).toLocaleDateString() : "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <Button asChild size="sm" variant="ghost"><Link to={`/contacts/${r.contact_id}`}>Open</Link></Button>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function SarTab() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const refresh = async () => {
        try { setItems((await api.get("/compliance/sar")).data); }
        catch (err) { toast.error(err.response?.data?.detail || "Access denied"); }
    };
    useEffect(() => { refresh(); }, []);

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white gap-1.5" data-testid="new-sar-btn">
                            <Plus className="w-4 h-4" /> Log SAR
                        </Button>
                    </DialogTrigger>
                    <NewSarDialog onDone={() => { setOpen(false); refresh(); }} />
                </Dialog>
            </div>
            {items.length === 0 ? (
                <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground" data-testid="sar-empty">
                    No SAR records logged. <Lock className="w-3.5 h-3.5 inline mx-1" /> Visible to admins and MLROs only.
                </div>
            ) : (
                <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="sar-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                                <th className="text-left px-4 py-3">Reference</th>
                                <th className="text-left px-4 py-3">Description</th>
                                <th className="text-left px-4 py-3">Action</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Logged</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`sar-row-${r.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs">{r.sar_reference || "—"}</td>
                                    <td className="px-4 py-3 text-xs max-w-md truncate">{r.description}</td>
                                    <td className="px-4 py-3 text-xs">{r.action_taken}</td>
                                    <td className="px-4 py-3"><Badge variant="outline" className="text-[10px] uppercase">{r.status}</Badge></td>
                                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{fmtDateRelative(r.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function NewSarDialog({ onDone }) {
    const [form, setForm] = useState({ description: "", action_taken: "internal_only", sar_reference: "", submitted_to_nca: false, status: "open", is_tipping_off_risk: false });
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.description.trim()) return toast.error("Description required");
        setLoading(true);
        try {
            await api.post("/compliance/sar", form);
            toast.success("SAR logged");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setLoading(false); }
    };
    return (
        <DialogContent data-testid="new-sar-dialog">
            <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                    <FileWarning className="w-4 h-4 text-orange-600" /> Log Suspicious Activity Report
                </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description *</Label>
                    <Textarea required rows={4} value={form.description} onChange={setF("description")} data-testid="sar-description-input" placeholder="Factual narrative — avoid speculation. Tipping-off risk applies." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Action taken</Label>
                        <Select value={form.action_taken} onValueChange={setF("action_taken")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="internal_only">Internal only</SelectItem>
                                <SelectItem value="consent">Consent (DAML/SARs Online)</SelectItem>
                                <SelectItem value="no_consent">No consent / declined</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">SAR reference</Label>
                        <Input value={form.sar_reference} onChange={setF("sar_reference")} placeholder="NCA-…" className="font-mono" />
                    </div>
                </div>
                <label className="flex items-start gap-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md p-2">
                    <input type="checkbox" checked={form.is_tipping_off_risk} onChange={setF("is_tipping_off_risk")} className="mt-0.5" data-testid="sar-tipping-off" />
                    <span><b>Tipping-off risk</b> — disclosing this SAR or its substance to the subject is a criminal offence under POCA 2002. Restrict access.</span>
                </label>
                <DialogFooter>
                    <Button type="submit" disabled={loading} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="sar-submit-btn">
                        {loading ? "Saving…" : "Log SAR"}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    );
}
